import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useSimStore } from '../store/simStore'
import type { InstanceSummary, RunHistory } from '../types'
import { AnimatedLogo } from './AnimatedLogo'

const DISPATCHERS = ['fifo', 'edd', 'spt', 'ltpt', 'cr', 'cp', 'meta', 'rolling', 'proactive']
const RT_OPTIONS = [
  { label: 'Full speed', value: 0 },
  { label: '0.01×', value: 0.01 },
  { label: '0.05×', value: 0.05 },
  { label: '0.1×',  value: 0.1  },
  { label: '0.5×',  value: 0.5  },
]

export function LobbyScreen() {
  const {
    instances, setInstances, instanceConfig, setInstanceConfig,
    selectedInstance, setSelectedInstance,
    selectedDispatcher, setSelectedDispatcher,
    rtFactor, setRtFactor,
    setRunId, setAppState, setRunHistory, runHistory, resetRun,
  } = useSimStore()

  const [tab, setTab] = useState<'select' | 'create'>('select')
  const [creating, setCreating] = useState(false)
  const [createForm, setCreateForm] = useState({
    name: '', scale: 'small', seed: 42,
    failure_profile: 'low', changeover_profile: 'simple',
  })
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    fetch('/api/instances')
      .then((r) => r.json())
      .then((list: InstanceSummary[]) => {
        setInstances(list)
        if (list.length && !selectedInstance) setSelectedInstance(list[0].name)
      })
      .catch(() => setError('Backend unreachable'))
    fetch('/api/runs?limit=10')
      .then((r) => r.json())
      .then((h: RunHistory[]) => setRunHistory(h))
      .catch(() => {})
  }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedInstance) return
    fetch(`/api/instances/${selectedInstance}/config`)
      .then((r) => r.json())
      .then(setInstanceConfig)
      .catch(() => {})
  }, [selectedInstance])  // eslint-disable-line react-hooks/exhaustive-deps

  async function handleStart() {
    if (!selectedInstance) return
    setStarting(true); setError('')
    resetRun()
    try {
      const resp = await fetch('/api/sim/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instance: selectedInstance, dispatcher: selectedDispatcher, rt_factor: rtFactor }),
      })
      if (!resp.ok) throw new Error(await resp.text())
      const data = await resp.json()
      setRunId(data.run_id)
      setAppState('LOADING')
    } catch (e: unknown) {
      setError(String(e))
    } finally {
      setStarting(false)
    }
  }

  async function handleCreate() {
    setCreating(true); setError('')
    try {
      if (!createForm.name.trim()) throw new Error('Name is required')
      const resp = await fetch('/api/instances', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createForm),
      })
      if (!resp.ok) throw new Error(await resp.text())
      const newCfg = await resp.json()
      const updated = await fetch('/api/instances').then((r) => r.json())
      setInstances(updated)
      setSelectedInstance(newCfg.name)
      setTab('select')
    } catch (e: unknown) {
      setError(String(e))
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="relative w-screen h-screen overflow-hidden bg-[#0d1117] text-white">
      {/* Vignette overlay */}
      <div className="absolute inset-0 bg-radial-gradient pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at center, transparent 30%, rgba(13,17,23,0.7) 100%)' }} />

      {/* Content layer */}
      <div className="absolute inset-0 flex flex-col items-center justify-center px-4 overflow-y-auto">

        {/* ── Animated logo ──────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: -24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="mb-6 flex flex-col items-center select-none"
        >
          <AnimatedLogo width={260} />

          {/* Word-mark */}
          <div className="mt-3 text-center">
            <span
              className="text-3xl font-black tracking-[0.22em] font-mono"
              style={{
                color: '#2dd4bf',
                textShadow: '0 0 32px rgba(45,212,191,0.65), 0 0 64px rgba(45,212,191,0.2)',
              }}
            >
              FACTORY
            </span>
            <span
              className="text-3xl font-black tracking-[0.22em] font-mono text-white"
              style={{ textShadow: '0 0 20px rgba(255,255,255,0.15)' }}
            >
              SIM
            </span>
          </div>
          <p className="mt-1 text-slate-500 text-xs tracking-[0.18em] uppercase">
            Flexible Job-Shop · SimPy · Real-Time Analytics
          </p>
        </motion.div>

        {/* Main card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="w-full max-w-3xl grid grid-cols-2 gap-6"
          style={{ filter: 'drop-shadow(0 0 40px rgba(45,212,191,0.08))' }}
        >
          {/* Left: config panel */}
          <div className="bg-[#161b22]/90 border border-[#30363d] rounded-xl p-5 backdrop-blur-sm">
            <div className="flex gap-2 mb-4">
              {(['select', 'create'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`px-3 py-1 rounded text-sm capitalize ${tab === t ? 'bg-teal-500 text-black font-bold' : 'bg-[#21262d] text-slate-400'}`}
                >
                  {t === 'select' ? '▷ Select Instance' : '＋ Create Instance'}
                </button>
              ))}
            </div>

            {tab === 'select' ? (
              <div className="space-y-3">
                <label className="block text-xs text-slate-400 uppercase tracking-wider">Instance</label>
                <select
                  value={selectedInstance}
                  onChange={(e) => setSelectedInstance(e.target.value)}
                  className="w-full bg-[#21262d] border border-[#30363d] rounded px-3 py-2 text-sm"
                >
                  {instances.map((i) => (
                    <option key={i.name} value={i.name}>{i.name}</option>
                  ))}
                </select>

                <label className="block text-xs text-slate-400 uppercase tracking-wider">Dispatcher</label>
                <select
                  value={selectedDispatcher}
                  onChange={(e) => setSelectedDispatcher(e.target.value)}
                  className="w-full bg-[#21262d] border border-[#30363d] rounded px-3 py-2 text-sm"
                >
                  {DISPATCHERS.map((d) => <option key={d}>{d}</option>)}
                </select>

                <label className="block text-xs text-slate-400 uppercase tracking-wider">Speed</label>
                <select
                  value={rtFactor}
                  onChange={(e) => setRtFactor(Number(e.target.value))}
                  className="w-full bg-[#21262d] border border-[#30363d] rounded px-3 py-2 text-sm"
                >
                  {RT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            ) : (
              <div className="space-y-3">
                {(['name','scale','failure_profile','changeover_profile'] as const).map((field) => (
                  <div key={field}>
                    <label className="block text-xs text-slate-400 mb-1 uppercase tracking-wider">{field.replace('_', ' ')}</label>
                    {field === 'scale' ? (
                      <select value={createForm.scale} onChange={(e) => setCreateForm((f) => ({ ...f, scale: e.target.value }))} className="w-full bg-[#21262d] border border-[#30363d] rounded px-3 py-2 text-sm">
                        {['small','medium','large'].map((s) => <option key={s}>{s}</option>)}
                      </select>
                    ) : field === 'failure_profile' ? (
                      <select value={createForm.failure_profile} onChange={(e) => setCreateForm((f) => ({ ...f, failure_profile: e.target.value }))} className="w-full bg-[#21262d] border border-[#30363d] rounded px-3 py-2 text-sm">
                        {['none','low','high'].map((s) => <option key={s}>{s}</option>)}
                      </select>
                    ) : field === 'changeover_profile' ? (
                      <select value={createForm.changeover_profile} onChange={(e) => setCreateForm((f) => ({ ...f, changeover_profile: e.target.value }))} className="w-full bg-[#21262d] border border-[#30363d] rounded px-3 py-2 text-sm">
                        {['none','simple','complex'].map((s) => <option key={s}>{s}</option>)}
                      </select>
                    ) : (
                      <input value={String(createForm[field])} onChange={(e) => setCreateForm((f) => ({ ...f, [field]: e.target.value }))} className="w-full bg-[#21262d] border border-[#30363d] rounded px-3 py-2 text-sm" placeholder={field === 'name' ? 'e.g. my_factory_s42' : ''} />
                    )}
                  </div>
                ))}
                <div>
                  <label className="block text-xs text-slate-400 mb-1 uppercase tracking-wider">Seed</label>
                  <input type="number" value={createForm.seed} onChange={(e) => setCreateForm((f) => ({ ...f, seed: Number(e.target.value) }))} className="w-full bg-[#21262d] border border-[#30363d] rounded px-3 py-2 text-sm" />
                </div>
                <button onClick={handleCreate} disabled={creating} className="w-full bg-teal-600 hover:bg-teal-500 disabled:opacity-50 rounded py-2 font-bold">
                  {creating ? 'Generating…' : '⚙ Generate Instance'}
                </button>
              </div>
            )}

            {error && <p className="text-rose-400 text-xs mt-3">{error}</p>}
          </div>

          {/* Right: instance details + start */}
          <div className="flex flex-col gap-4">
            {instanceConfig && (
              <div className="bg-[#161b22]/90 border border-[#30363d] rounded-xl p-4 text-sm space-y-1 backdrop-blur-sm">
                <div className="text-teal-400 font-bold text-base mb-2">{instanceConfig.name}</div>
                {[
                  ['Scale',    instanceConfig.scale],
                  ['Orders',   instanceConfig.n_orders],
                  ['Machines', instanceConfig.n_machines],
                  ['Workers',  instanceConfig.n_workers],
                  ['Horizon',  `${instanceConfig.horizon} min`],
                  ['Failures', instanceConfig.failure_profile],
                  ['SDST',     instanceConfig.changeover_profile],
                ].map(([k, v]) => (
                  <div key={String(k)} className="flex justify-between text-xs">
                    <span className="text-slate-400">{k}</span>
                    <span className="font-mono">{v}</span>
                  </div>
                ))}
              </div>
            )}

            <motion.button
              onClick={handleStart}
              disabled={starting || !selectedInstance}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              className="w-full py-4 text-lg font-bold rounded-xl disabled:opacity-50 text-black transition-colors"
              style={{
                background: 'linear-gradient(135deg, #2dd4bf 0%, #0891b2 100%)',
                boxShadow: '0 0 30px rgba(45,212,191,0.35)',
              }}
            >
              {starting ? '▶ Starting…' : '▶  START SIMULATION'}
            </motion.button>
          </div>
        </motion.div>

        {/* Recent runs */}
        {runHistory.length > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
            className="mt-6 w-full max-w-3xl"
          >
            <h3 className="text-xs text-slate-500 uppercase tracking-wider mb-2">Recent Runs</h3>
            <div className="flex gap-2 flex-wrap">
              {runHistory.slice(0, 5).map((r) => (
                <div key={r.run_id} className="bg-[#161b22]/80 border border-[#30363d] rounded px-3 py-1 text-xs backdrop-blur-sm">
                  <span className="text-teal-400">{r.instance}</span>
                  <span className="text-slate-500 mx-1">/</span>
                  <span>{r.dispatcher}</span>
                  {r.kpis?.cpi != null && <span className="ml-2 text-emerald-400">CPI {r.kpis.cpi.toFixed(3)}</span>}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        <p className="mt-6 text-slate-600 text-xs">Space: pause  +/-: speed  R: reset camera</p>
      </div>
    </div>
  )
}
